import * as React from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import { StyleSheet } from 'react-native';
import Typography from '@mui/material/Typography';
import { UserSummary } from '../../types/global/helper';
import { useThemeContext } from '../../hook/useThemeManager';
import { ColorScheme, theme } from '../../constants/theme';
import VerificationBadge from '../CustomSVGIcons/VerificationBadge';
import { createFullName } from '../../utils/AppUtils';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { DisplayChipGroup } from '../Chip/DisplayChipGroup';
import { avatar_female, avatar_male } from '../../asset/images';
import { ITalentData } from '../../redux/talent/types';
import { Box } from '@mui/material';
import { IUserProfile } from '../../redux/user/actions';
import { FullScreenMoreMenu } from '../More/FullScreenMoreMenu';
import { TalentEditor, ITalentDetails } from '../TalentEditor/TalentEditor';
import { useState } from 'react';
import CustomLazyLoadImage from '../Image/CustomLazyLoadImage';

export interface ITalentsCard {
    talent: ITalentData;
    onClickUserCard?: (talent: ITalentData) => void;
    onDeleteTalent?: (talent: ITalentData) => void;
    onEditTalent?: (details: ITalentDetails) => void;
    showIconOnly?: boolean;
    iconSx?: object;
}

export const TalentsCard: React.FC<ITalentsCard> = ({
    talent,
    onClickUserCard,
    onDeleteTalent,
    onEditTalent,
    showIconOnly,
    iconSx,
}) => {
    const { themeStyle, device } = useThemeContext();
    const styles = dynamicStyles(themeStyle);
    const selectedAvatar = talent?.talent?.gender === 'female' ? avatar_female : avatar_male;
    const talentAsIUserProfile = talent.talent as unknown as IUserProfile;
    const [open, setOpen] = useState(false);

    const menu = [
        {
            id: 'edit',
            title: 'Edit Tags & Notes',
        },
        {
            id: 'delete',
            title: 'Remove Talent',
        },
    ];

    const handleMoreItemClick = (moreType: string) => {
        if (moreType.toLowerCase() === 'edit') {
            /** Copy this link or share it or something */
            setOpen(!open);
        }

        if (moreType.toLowerCase() === 'delete') {
            onDeleteTalent && onDeleteTalent(talent);
        }

        if (moreType.toLowerCase() === 'view') {
            onClickUserCard && onClickUserCard(talent);
        }
    };

    if (showIconOnly) {
        menu.unshift({
            id: 'view',
            title: 'View Talent',
        });
        return (
            <>
                <TalentEditor
                    open={open}
                    onClose={() => setOpen(!open)}
                    onSubmit={details => onEditTalent && onEditTalent(details)}
                    initialValue={talent}
                />
                <FullScreenMoreMenu
                    customIconSx={iconSx}
                    menu={menu}
                    onClick={handleMoreItemClick}
                    title={createFullName(talent.talent as unknown as UserSummary)}
                />
            </>
        );
    }

    return (
        <Card
            sx={{
                borderRadius: '10px',
                maxWidth: {
                    xs: '100%',
                    sm: 300,
                },
                height: {
                    xs: '300px',
                    sm: '340px',
                },
                backgroundColor: theme[themeStyle].deep_grey_lighter,
                position: 'relative',
                cursor: 'pointer',
                overflow: 'hidden',
            }}>
            <TalentEditor
                open={open}
                onClose={() => setOpen(!open)}
                onSubmit={details => onEditTalent && onEditTalent(details)}
                initialValue={talent}
            />
            <FullScreenMoreMenu
                customIconSx={{
                    top: 0,
                    right: 0,
                    position: 'absolute',
                    zIndex: 10,
                }}
                sx={{
                    backgroundColor: theme[themeStyle].true_black,
                    color: theme[themeStyle].true_white,
                    padding: '2px',
                    height: '30px',
                    width: '30px',
                    borderRadius: '4px',
                }}
                menu={menu}
                onClick={handleMoreItemClick}
                title={createFullName(talent.talent as unknown as UserSummary)}
            />

            <CustomLazyLoadImage
                effect="blur"
                src={talentAsIUserProfile?.profile_picture ?? selectedAvatar}
                alt="talent_image"
                width={'100%'}
                height={'100%'}
                style={{
                    position: 'absolute',
                    zIndex: -1,
                    objectFit: 'cover',
                    height: device.isMobile ? '300px' : '100%',
                }}
            />

            <CardContent
                onClick={() => {
                    onClickUserCard && onClickUserCard(talent);
                }}
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-end',
                    background: 'linear-gradient(transparent, black)',
                }}>
                <Typography
                    variant="h5"
                    component="span"
                    color="white"
                    style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: '150px',
                        ...styles.textHandle,
                    }}>
                    {createFullName(talent.talent as unknown as UserSummary)}
                </Typography>

                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Typography
                        sx={{
                            textOverflow: 'ellipsis',
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                            maxWidth: '150px',
                        }}
                        style={styles.share}>
                        @{talent?.talent?.userName}
                    </Typography>
                    {talent?.talent?.verified && <VerificationBadge />}
                </div>
                <Box>
                    {talent.tags && (
                        <DisplayChipGroup
                            items={talent?.tags}
                            maxChips={2}
                            fontSize={8}
                            style={{
                                marginTop: '5px',
                            }}
                        />
                    )}
                    {talent.note && (
                        <Typography
                            sx={{
                                listStylePosition: 'outside',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                maxWidth: '200px',
                            }}
                            style={styles.share}>
                            {talent?.note}
                        </Typography>
                    )}
                </Box>
            </CardContent>
        </Card>
    );
};

const dynamicStyles = (colorScheme: ColorScheme) =>
    StyleSheet.create({
        shareBox: {
            width: 'fit-content',
            height: 'full',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
        },
        flexRow: {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
        },
        flexColumn: {
            flexDirection: 'column',
            alignItems: 'flex-start',
            justifyContent: 'center',
        },
        share: {
            fontSize: 12,
            fontWeight: '500',
            fontFamily: FONT_ENUM.MAIN_REGULAR,
            color: theme[colorScheme].primary_white_orange,
        },
        textHandle: {
            fontSize: 16,
            fontWeight: '500',
            fontFamily: FONT_ENUM.REGULAR_HEADER,
        },
    });
